import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { refreshToken } from '../../../../api';

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (error: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (token) {
            resolve(token);
        } else {
            reject(error);
        }
    });
    failedQueue = [];
};

export const setupRefreshTokenInterceptor = (instance: AxiosInstance) => {
    // Mark refresh token requests so the response interceptor can skip them
    instance.interceptors.request.use((config) => {
        if (config.data && typeof config.data === 'object' && config.data.grant_type === 'refresh_token') {
            (config as AxiosRequestConfig & { _isRefreshRequest?: boolean })._isRefreshRequest = true;
        }
        return config;
    });

    instance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as AxiosRequestConfig & {
                _retry?: boolean;
                _isRefreshRequest?: boolean;
            };

            // Skip interceptor for refresh token requests
            if (originalRequest._isRefreshRequest || error?.config?.url?.includes('/token')) {
                return Promise.reject(error);
            }

            if (error.response?.status !== 401 || originalRequest._retry) {
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                    }
                    return instance(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const currentRefreshToken = localStorage.getItem('refreshToken');

                if (!currentRefreshToken) {
                    throw new Error('No refresh token available');
                }

                const response = await refreshToken(currentRefreshToken);

                const { access_token, refresh_token } = response.data;

                localStorage.setItem('accessToken', access_token);
                localStorage.setItem('refreshToken', refresh_token);

                processQueue(null, access_token);

                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${access_token}`;
                }

                return instance(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                window.dispatchEvent(new CustomEvent('error-refreshing-token'));
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
    );
};
